import { z } from "zod";
import { Id, Timestamp } from "./common.js";

/** Calendar date, YYYY-MM-DD. */
export const IsoDate = z.iso.date();

export const EventKind = z.enum(["event", "birthday"]);
export type EventKind = z.infer<typeof EventKind>;

/** Calendar palette keys (DESIGN.md "Calendar colours"); never hex values. */
export const CalendarColor = z.enum(["cal-1", "cal-2", "cal-3", "cal-4", "cal-5", "cal-6"]);
export type CalendarColor = z.infer<typeof CalendarColor>;

/** Max days between `from` and `to` of an items request (inclusive range of 62 days). */
export const MAX_ITEMS_RANGE_DAYS = 61;
export const MAX_FEEDS_PER_USER = 20;

const DAY_MS = 86_400_000;
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / DAY_MS);

/**
 * Timing shared by events and items: all-day items have dates (end inclusive), timed items
 * have UTC instants. The other pair is null.
 */
const timing = {
  allDay: z.boolean(),
  startDate: IsoDate.nullable(),
  endDate: IsoDate.nullable(),
  startsAt: Timestamp.nullable(),
  endsAt: Timestamp.nullable(),
};

export const CalendarEvent = z.object({
  id: Id,
  kind: EventKind,
  title: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  ...timing,
  birthYear: z.number().int().nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type CalendarEvent = z.infer<typeof CalendarEvent>;

/**
 * Create and update take the whole event, so the timing rules are always checked as a
 * unit: all-day → dates only, end ≥ start; timed → instants only, end > start; birthday →
 * one all-day date, with an optional birth year not after it.
 */
export const EventInput = z
  .object({
    kind: EventKind,
    title: z.string().trim().min(1).max(200),
    description: z.string().max(5000).nullish(),
    location: z.string().trim().max(200).nullish(),
    allDay: z.boolean(),
    startDate: IsoDate.nullish(),
    endDate: IsoDate.nullish(),
    startsAt: Timestamp.nullish(),
    endsAt: Timestamp.nullish(),
    birthYear: z.number().int().min(1900).max(2100).nullish(),
  })
  .superRefine((e, ctx) => {
    const issue = (message: string, path: string) =>
      ctx.addIssue({ code: "custom", message, path: [path] });
    if (e.allDay) {
      if (!e.startDate) issue("An all-day event needs a start date", "startDate");
      if (!e.endDate) issue("An all-day event needs an end date", "endDate");
      if (e.startsAt || e.endsAt) issue("An all-day event has no times", "startsAt");
      if (e.startDate && e.endDate && e.endDate < e.startDate) {
        issue("The end date is before the start date", "endDate");
      }
    } else {
      if (!e.startsAt) issue("A timed event needs a start", "startsAt");
      if (!e.endsAt) issue("A timed event needs an end", "endsAt");
      if (e.startDate || e.endDate) issue("A timed event has no dates", "startDate");
      if (e.startsAt && e.endsAt && Date.parse(e.endsAt) <= Date.parse(e.startsAt)) {
        issue("The end is not after the start", "endsAt");
      }
    }
    if (e.kind === "birthday") {
      if (!e.allDay) issue("A birthday is all-day", "allDay");
      if (e.startDate !== e.endDate) issue("A birthday is one day", "endDate");
      if (e.birthYear && e.startDate && e.birthYear > Number(e.startDate.slice(0, 4))) {
        issue("The birth year is after the date", "birthYear");
      }
    } else if (e.birthYear != null) {
      issue("Only birthdays have a birth year", "birthYear");
    }
  });
export type EventInput = z.infer<typeof EventInput>;

export const CalendarEventParams = z.object({ id: Id });

export const ItemsQuery = z
  .object({ from: IsoDate, to: IsoDate })
  .refine((q) => q.to >= q.from, { message: "`to` is before `from`", path: ["to"] })
  .refine((q) => daysBetween(q.from, q.to) <= MAX_ITEMS_RANGE_DAYS, {
    message: `The range is longer than ${MAX_ITEMS_RANGE_DAYS + 1} days`,
    path: ["to"],
  });
export type ItemsQuery = z.infer<typeof ItemsQuery>;

const EventItem = z.object({
  source: z.literal("event"),
  /** Unique per occurrence (a birthday has one item per year). */
  id: z.string(),
  title: z.string(),
  ...timing,
  event: CalendarEvent,
  /** Birthdays with a birth year: the age on that day. */
  age: z.number().int().nullable(),
});

const FeedItem = z.object({
  source: z.literal("feed"),
  id: z.string(),
  title: z.string(),
  ...timing,
  feedId: Id,
  description: z.string().nullable(),
  location: z.string().nullable(),
});

const HolidayItem = z.object({
  source: z.literal("holiday"),
  id: z.string(),
  title: z.string(),
  holidayType: z.enum(["public", "school"]),
  allDay: z.literal(true),
  startDate: IsoDate,
  endDate: IsoDate,
  startsAt: z.null(),
  endsAt: z.null(),
});

export const CalendarItem = z.discriminatedUnion("source", [EventItem, FeedItem, HolidayItem]);
export type CalendarItem = z.infer<typeof CalendarItem>;
export type EventItem = z.infer<typeof EventItem>;
export type FeedItem = z.infer<typeof FeedItem>;
export type HolidayItem = z.infer<typeof HolidayItem>;

export const CalendarItems = z.object({ items: z.array(CalendarItem) });
export type CalendarItems = z.infer<typeof CalendarItems>;

// --- Feeds ---

/** Why a feed could not be loaded. Never raw server output. */
export const FeedError = z.enum([
  "invalid_url",
  "blocked_address",
  "unreachable",
  "timeout",
  "too_large",
  "http_error",
  "invalid_ics",
]);
export type FeedError = z.infer<typeof FeedError>;

/** https or webcal (fetched as https), without credentials in the URL. */
export const FeedUrl = z
  .string()
  .trim()
  .max(2048)
  .refine(
    (value) => {
      try {
        const url = new URL(value);
        return (
          (url.protocol === "https:" || url.protocol === "webcal:") &&
          url.hostname !== "" &&
          url.username === "" &&
          url.password === ""
        );
      } catch {
        return false;
      }
    },
    { message: "Use an https:// or webcal:// address" },
  );

export const CalendarFeed = z.object({
  id: Id,
  name: z.string(),
  /** Scheme and host only (the full URL may hold a private token). */
  urlPreview: z.string(),
  color: CalendarColor,
  visible: z.boolean(),
  lastFetchedAt: Timestamp.nullable(),
  lastSuccessAt: Timestamp.nullable(),
  lastError: FeedError.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type CalendarFeed = z.infer<typeof CalendarFeed>;

const FeedName = z.string().trim().min(1).max(100);

export const CreateFeed = z.object({ url: FeedUrl, name: FeedName, color: CalendarColor });
export type CreateFeed = z.infer<typeof CreateFeed>;

export const UpdateFeed = z
  .object({ url: FeedUrl, name: FeedName, color: CalendarColor, visible: z.boolean() })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to update" });
export type UpdateFeed = z.infer<typeof UpdateFeed>;

export const CalendarFeedParams = z.object({ id: Id });

export const CalendarFeedList = z.object({ feeds: z.array(CalendarFeed) });
export type CalendarFeedList = z.infer<typeof CalendarFeedList>;
